import { useMutation } from '@tanstack/react-query'
import { Sparkles } from 'lucide-react'

import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'

import { useAssistantEnabled, type AssistantText } from './api'

const UNAVAILABLE = 'The assistant could not answer right now. Try again later.'

/**
 * A card that asks the assistant for a draft on demand.
 *
 * Nothing is requested until the button is pressed -- each answer is a billed call -- and
 * the card is absent entirely when the assistant is off. The answer is labelled as a draft
 * because that is what it is: a person reads it and decides.
 */
export function AssistantPanel({
  title,
  description,
  buttonLabel,
  ask,
}: {
  title: string
  description: string
  buttonLabel: string
  ask: () => Promise<AssistantText>
}) {
  const enabled = useAssistantEnabled()
  const answer = useMutation({ mutationFn: ask })

  if (!enabled) return null

  const text = answer.data?.text
  return (
    <Card>
      <CardHeader
        title={title}
        description={description}
        action={
          <Button
            variant="secondary"
            size="sm"
            onClick={() => answer.mutate()}
            loading={answer.isPending}
          >
            <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
            {answer.data ? 'Ask again' : buttonLabel}
          </Button>
        }
      />
      {(answer.isSuccess || answer.isError) && (
        <CardBody>
          {text ? (
            <>
              <p className="whitespace-pre-line text-sm text-slate-700 dark:text-slate-300">
                {text}
              </p>
              <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">
                AI draft — check it before you use it.
              </p>
            </>
          ) : (
            <p className="text-sm text-slate-500 dark:text-slate-400">{UNAVAILABLE}</p>
          )}
        </CardBody>
      )}
    </Card>
  )
}

/**
 * "Why?" beside a flagged price. Inline and small: it sits in a table cell.
 */
export function ExplainPrice({ ask }: { ask: () => Promise<AssistantText> }) {
  const enabled = useAssistantEnabled()
  const answer = useMutation({ mutationFn: ask })

  if (!enabled) return null

  if (answer.isSuccess || answer.isError) {
    return (
      <p className="mt-1 max-w-md whitespace-pre-line text-xs font-normal text-amber-800 dark:text-amber-300">
        {answer.data?.text ?? UNAVAILABLE}
      </p>
    )
  }
  return (
    <button
      type="button"
      className="inline-flex items-center gap-1 text-xs font-normal text-brand-600 hover:underline disabled:opacity-50 dark:text-brand-400"
      onClick={() => answer.mutate()}
      disabled={answer.isPending}
    >
      <Sparkles className="h-3 w-3" aria-hidden="true" />
      {answer.isPending ? 'Explaining…' : 'Why?'}
    </button>
  )
}
